/** 1.3.0 portal: one submission's result (preview, QC findings, files, comments, withdraw) and My uploads. */
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { IntakeNoteDTO } from '@prism/contracts';
import { t } from '../i18n/en';
import { errorText, portalApi } from '../client';
import { formatBytes, formatDate, formatDateTime } from '../format';
import { Chip, Dialog, Loading, Thread, useLoad } from '../components';
import type { PortalSubmission } from '../types';

const processing = (s: PortalSubmission) =>
  s.media ? ['registered', 'processing'].includes(s.media.status) || (s.qc ? ['queued', 'running'].includes(s.qc.status) : false) : false;

export function ResultPage() {
  const { id } = useParams();
  const { data: s, error, reload } = useLoad<PortalSubmission>(`/submissions/${id}`, { poll: processing, pollMs: 4000 });
  const notes = useLoad<{ items: IntakeNoteDTO[] }>(`/submissions/${id}/notes`, { poll: () => true, pollMs: 15000 });
  const video = useRef<HTMLVideoElement>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<unknown>(null);
  if (!s) return <Loading error={error} retry={reload} />;
  const seek = (at: number) => {
    const v = video.current;
    if (!v) return;
    v.currentTime = at;
    void v.play().catch(() => undefined);
  };
  const withdraw = async () => {
    try {
      await portalApi(`/submissions/${s.id}/withdraw`, { method: 'POST', json: { reason } });
      setWithdrawing(false);
      reload();
      notes.reload();
    } catch (e) {
      setErr(e);
    }
  };
  return (
    <>
      <p>{s.assignmentId ? <Link to={`/deliveries/${s.assignmentId}`} className="p-link">← {t('portal.back')}</Link> : <Link to="/uploads" className="p-link">← {t('portal.myUploads')}</Link>}</p>
      <div className="p-page-head">
        <div className="p-row p-between">
          <h1>{s.title}</h1>
          <Chip kind="submission" state={s.state} />
        </div>
        <p className="p-muted p-small">{formatDateTime(s.createdAt)}{s.rights ? ` · ${t('portal.result.rightsAccepted', { date: formatDate(s.rights.acceptedAt), version: s.rights.version })}` : ''}</p>
      </div>

      <div className="p-result">
        <section className="p-card p-preview" aria-label={t('portal.result.preview')}>
          <h3>{t('portal.result.preview')}</h3>
          {s.media?.previewUrl ? (
            <video ref={video} controls playsInline preload="metadata" src={s.media.previewUrl} poster={s.media.posterUrl ?? undefined} data-testid="preview" />
          ) : (
            <p className="p-muted">{t('portal.result.noPreview')}</p>
          )}
          {s.media ? (
            <p className="p-small p-muted"><span className="p-file">{s.media.filename}</span>{s.media.sizeBytes ? ` · ${formatBytes(s.media.sizeBytes)}` : ''} · {t(`portal.state.processing.${s.media.status}`)}</p>
          ) : null}
        </section>

        <section className="p-card" aria-label={t('portal.result.qc')} data-testid="qc">
          <h3>{t('portal.result.qc')}</h3>
          {!s.qc ? <p className="p-muted">{s.media && ['registered', 'processing'].includes(s.media.status) ? t('portal.result.qcPending') : t('portal.result.qcNone')}</p> : (
            <>
              <div className="p-row">
                <Chip kind="qc" state={s.qc.effectiveStatus} />
                {['passed', 'warnings', 'failed'].includes(s.qc.status) ? (
                  <span className="p-small">{t('portal.result.qcSummary', { result: s.qc.profile, errors: s.qc.errors, warnings: s.qc.warnings })}</span>
                ) : null}
              </div>
              {['passed', 'warnings', 'failed', 'error'].includes(s.qc.status) ? (
                <>
                  <h4>{t('portal.result.findings')}</h4>
                  {s.qc.findings.length === 0 ? <p className="p-muted">{t('portal.result.noFindings')}</p> : (
                    <ol className="p-findings">
                      {s.qc.findings.map((f, i) => (
                        <li key={i} className={`p-finding ${f.severity}`}>
                          {f.atSeconds !== null && s.media?.previewUrl ? (
                            <button type="button" className="p-tc" onClick={() => seek(f.atSeconds!)} aria-label={t('portal.result.jump', { tc: f.timecode ?? `${f.atSeconds.toFixed(1)} s` })}>
                              {f.timecode ?? `${f.atSeconds.toFixed(1)} s`}
                            </button>
                          ) : <span className="p-tc">{f.timecode ?? t('portal.result.wholeFile')}</span>}
                          <span className={`p-chip p-chip-${f.severity === 'error' ? 'bad' : 'warn'}`}>{t(`portal.severity.${f.severity}`)}</span>
                          <span><strong>{f.checkLabel}</strong> {f.message}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                  <p><a className="p-btn" href={s.qc.reportUrl} target="_blank" rel="noreferrer" data-testid="qc-report">{t('portal.result.report')}</a></p>
                </>
              ) : null}
            </>
          )}
        </section>
      </div>

      {s.attachments.length ? (
        <section className="p-card">
          <h3>{t('portal.result.files')}</h3>
          <ul className="p-files">
            {s.attachments.map((a) => (
              <li key={a.id}><a href={a.url} className="p-link">{a.filename}</a> <span className="p-muted p-small">{formatBytes(a.sizeBytes)}</span></li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="p-card">
        <h3>{t('portal.comments.title')}</h3>
        <Thread submissionId={s.id} itemId={null} notes={notes.data?.items ?? []} onPosted={notes.reload} title={t('portal.comments.whole')} />
        {s.items.map((i) => (
          <Thread key={i.id} submissionId={s.id} itemId={i.id} notes={notes.data?.items ?? []} onPosted={notes.reload} title={t('portal.comments.itemTitle', { item: i.label })} label={i.label} />
        ))}
      </section>

      {s.canWithdraw ? (
        <p className="p-row p-end"><button type="button" className="p-btn p-btn-danger" onClick={() => setWithdrawing(true)} data-testid="withdraw">{t('portal.withdraw')}</button></p>
      ) : null}
      <Dialog open={withdrawing} title={t('portal.withdraw.title')} onClose={() => setWithdrawing(false)}>
        <p>{t('portal.withdraw.body')}</p>
        <label className="p-field">
          <span>{t('portal.withdraw.reason')}</span>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} />
        </label>
        {err ? <p className="p-bad" role="alert">{errorText(err)}</p> : null}
        <div className="p-row p-end">
          <button type="button" className="p-btn p-btn-quiet" onClick={() => setWithdrawing(false)}>{t('common.cancel')}</button>
          <button type="button" className="p-btn p-btn-danger" onClick={() => void withdraw()} data-testid="withdraw-confirm">{t('portal.withdraw.confirm')}</button>
        </div>
      </Dialog>
    </>
  );
}

export function UploadsPage() {
  const { data, error, reload } = useLoad<{ items: PortalSubmission[] }>('/submissions?kind=open', { poll: (v) => v.items.some(processing), pollMs: 6000 });
  return (
    <>
      <div className="p-page-head"><h1>{t('portal.myUploads')}</h1></div>
      {!data ? <Loading error={error} retry={reload} /> : data.items.length === 0 ? <p className="p-empty">{t('portal.uploads.empty')}</p> : (
        <ul className="p-list">
          {data.items.map((s) => (
            <li key={s.id} className="p-list-row">
              <Link to={`/submissions/${s.id}`} className="p-list-main">
                <strong>{s.title}</strong>
                <span className="p-muted p-small">{s.media?.filename ?? ''} · {formatDateTime(s.createdAt)}</span>
              </Link>
              <span className="p-row">
                {s.qc ? <Chip kind="qc" state={s.qc.effectiveStatus} /> : null}
                <Chip kind="submission" state={s.state} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
